import { Fragment, ReactNode, useState } from 'react';
import { signInWithRedirect } from 'aws-amplify/auth';
import { NextPage } from 'next';

import { field, useForm, useSubmit, validate } from '@reckon-web/forms';
import { Flex } from '@reckon-web/flex';
import { Text } from '@reckon-web/text';
import { Button } from '@reckon-web/button';
import { Box } from '@reckon-web/box';
import { Notice } from '@reckon-web/notice';

import { TextField } from '../generic/TextField';
import { checkSAML } from '../../lib/api';

export const LoginForm: NextPage<{
  onsubmit?: any;
  loading: boolean;
  setLoading?: any;
  errorMessage?: string;
  setErrorMessage?: any;
  children: ReactNode;
}> = ({
  onsubmit,
  errorMessage,
  loading,
  setLoading,
  setErrorMessage,
  children,
}) => {
  let [pwdRequired, setPwdRequired] = useState<boolean>(false);
  let loginForm = field.object({
    username: field.text({
      validate: validate((value) => {
        if (!value.length) return validate.error('Enter your username');
        return value;
      }),
    }),
    password: field.text({
      validate: validate((value) => {
        if (pwdRequired && !value.length)
          return validate.error('Enter your password');
        return value;
      }),
    }),
  });

  let form = useForm(loginForm);
  const submit = useSubmit(form, async (value) => {
    if (!value.username || (pwdRequired && !value.password)) return;
    if (pwdRequired && value.password) {
      setLoading(true);
      onsubmit(value);
    }
    if (!pwdRequired) {
      setLoading(true);
      const provider = (await checkSAML(value.username)) as string;
      if (provider) {
        signInWithRedirect({
          provider: { custom: provider },
        });
      } else {
        setLoading(false);
        setPwdRequired(true);
      }
    }
  });
  return (
    <Fragment>
      <form
        id="login-form"
        onChange={() => setErrorMessage('')}
        onSubmit={submit}
      >
        {errorMessage ? (
          <Box paddingBottom="xlarge">
            <Notice tone="critical">
              {errorMessage ? <Text>{errorMessage}</Text> : ''}
            </Notice>
          </Box>
        ) : null}

        <Flex
          direction="column"
          gap="medium"
          alignItems="center"
          padding="xlarge"
        >
          <Box
            paddingX="large"
            paddingY="medium"
            borderRadius="small"
            width={400}
          >
            <TextField
              type="text"
              label="Username"
              labelVisible={false}
              placeholder="Enter username"
              {...form.fields.username.props}
            />
            {pwdRequired ? (
              <Box marginTop="small">
                <TextField
                  type="password"
                  label="Password"
                  labelVisible={false}
                  placeholder="Enter password"
                  {...form.fields.password.props}
                />
              </Box>
            ) : (
              ''
            )}
            <Box marginTop="medium">
              <Button
                id="login"
                disabled={form.validity !== 'valid'}
                type="submit"
                size="medium"
                loading={loading}
                block
                label="Log in"
              />
            </Box>
            {children}
          </Box>
        </Flex>
      </form>
    </Fragment>
  );
};
